/*
 * Copyright (C) 2026 Discorch Contributors
 *
 * This file is part of Discorch.
 *
 * Discorch is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Discorch is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Discorch. If not, see <https://www.gnu.org/licenses/>.
 */

import { CheckboxWithState } from "@/components/studio/MessageView/CheckboxWithState";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useSearch } from "@/hooks/useSearch";
import { sanitizeForSearch } from "@/lib/process-package";
import { matchesSearchQuery, parseSearchQuery } from "@/lib/search-query";
import { useSelection } from "@/lib/stores/selection";
import { cn } from "@/lib/utils";
import type { ProcessedChannel } from "@/types";
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  ArrowUpDown,
  CheckSquare,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  FolderSearch,
  Hash,
  Loader2,
  MessageSquare,
  Paperclip,
  Radio,
  Users,
  Video,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";

interface ChannelListProps {
  channels: ProcessedChannel[];
  onChannelSelect?: (channel: ProcessedChannel) => void;
  selectedChannel?: ProcessedChannel;
}

const CHANNEL_ICONS = {
  GUILD_TEXT: Hash,
  DM: Users,
  GROUP_DM: Users,
  GUILD_VOICE: Radio,
  GUILD_ANNOUNCEMENT: MessageSquare,
  ANNOUNCEMENT_THREAD: MessageSquare,
  PUBLIC_THREAD: MessageSquare,
  PRIVATE_THREAD: MessageSquare,
  GUILD_STAGE_VOICE: Radio,
  GUILD_MEDIA: Video,
} as const;

function summarizeItems(items: string[], maxItems = 3) {
  if (items.length === 0) {
    return "";
  }

  const displayed = items.slice(0, maxItems).join(", ");
  return items.length > maxItems
    ? `${displayed} (+${items.length - maxItems} more)`
    : displayed;
}

async function processInChunks<T>(
  items: T[],
  processor: (chunk: T[]) => Promise<void> | void,
  chunkSize: number = 1000,
): Promise<void> {
  for (let i = 0; i < items.length; i += chunkSize) {
    const chunk = items.slice(i, i + chunkSize);
    await processor(chunk);
    if (i + chunkSize < items.length) {
      await new Promise((resolve) => setTimeout(resolve, 0));
    }
  }
}

export function ChannelList({
  channels,
  onChannelSelect,
  selectedChannel,
}: ChannelListProps) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [processedData, setProcessedData] = useState<ProcessedChannel[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const filters = useSearch((state) => state.filters);
  const clearFilters = useSearch((state) => state.clearFilters);
  const activeFilters = useMemo(() => {
    const entries: { label: string; detail?: string }[] = [];

    const trimmedTerms = filters.terms?.trim();
    if (trimmedTerms) {
      entries.push({
        label: "Search",
        detail:
          trimmedTerms.length > 45
            ? `${trimmedTerms.slice(0, 42)}…`
            : trimmedTerms,
      });
    }

    if (filters.dateRange?.from || filters.dateRange?.to) {
      const formatter = new Intl.DateTimeFormat(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      });

      const fromLabel = filters.dateRange?.from
        ? formatter.format(filters.dateRange.from)
        : undefined;
      const toLabel = filters.dateRange?.to
        ? formatter.format(filters.dateRange.to)
        : undefined;

      let detail = "";
      if (fromLabel && toLabel) {
        detail = `${fromLabel} – ${toLabel}`;
      } else if (fromLabel) {
        detail = `From ${fromLabel}`;
      } else if (toLabel) {
        detail = `Until ${toLabel}`;
      }

      entries.push({ label: "Date range", detail });
    }

    if (filters.channelTypes.length > 0) {
      entries.push({
        label: "Channel types",
        detail: summarizeItems(filters.channelTypes),
      });
    }

    if (filters.serverNames.length > 0) {
      entries.push({
        label: "Servers",
        detail: summarizeItems(filters.serverNames),
      });
    }

    if (filters.hasAttachments) {
      entries.push({ label: "Has attachments" });
    }

    if (filters.showLeftServers) {
      entries.push({ label: "Left servers only" });
    }

    return entries;
  }, [filters]);
  const hasActiveFilters = activeFilters.length > 0;
  const { toggleChannel, getChannelSelectionState } = useSelection();

  const columns = useMemo<ColumnDef<ProcessedChannel>[]>(
    () => [
      {
        id: "select",
        size: 40,
        header: ({ table }) => {
          const visibleRows = table.getRowModel().rows;
          const selectedCount = visibleRows.reduce((count, row) => {
            const state = getChannelSelectionState(
              row.original.id,
              row.original.messages,
            );
            return count + (state.selected ? 1 : 0);
          }, 0);

          const allSelected =
            selectedCount === visibleRows.length && visibleRows.length > 0;
          const someSelected =
            selectedCount > 0 && selectedCount < visibleRows.length;

          function handleSelectAll(checked: boolean) {
            visibleRows.forEach((row) => {
              toggleChannel(row.original.id, row.original.messages, checked);
            });
          }

          return (
            <div className="flex h-full items-center justify-center">
              <CheckboxWithState
                checked={allSelected}
                indeterminate={someSelected}
                onCheckedChange={handleSelectAll}
                aria-label="Select all visible channels"
              />
            </div>
          );
        },
        cell: ({ row }) => {
          const channel = row.original;
          const selectionState = getChannelSelectionState(
            channel.id,
            channel.messages,
          );

          return (
            <div className="flex h-full items-center justify-center">
              <CheckboxWithState
                checked={selectionState.selected}
                indeterminate={selectionState.indeterminate}
                onCheckedChange={(checked) => {
                  toggleChannel(channel.id, channel.messages, checked);
                }}
                onClick={(e) => e.stopPropagation()}
                aria-label={`Select ${channel.displayName}`}
              />
            </div>
          );
        },
        enableSorting: false,
      },
      {
        accessorKey: "displayName",
        size: 320,
        header: ({ column }) => (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="w-full justify-start px-2"
          >
            Channel
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          const channel = row.original;
          const Icon =
            CHANNEL_ICONS[channel.type as keyof typeof CHANNEL_ICONS] || Hash;

          return (
            <div className="flex items-center gap-2">
              <Icon className="text-muted-foreground h-4 w-4 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">{channel.displayName}</p>
                {channel.serverName && (
                  <p className="text-muted-foreground truncate text-xs">
                    {channel.serverName}
                    {channel.channelCategory && ` · ${channel.channelCategory}`}
                  </p>
                )}
              </div>
            </div>
          );
        },
      },
      {
        id: "messages",
        size: 120,
        accessorFn: (row) => row.messageCount,
        header: ({ column }) => (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="w-full justify-end px-2"
          >
            Messages
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => (
          <div className="space-y-1 text-right">
            <p className="font-medium tabular-nums">
              {row.original.messageCount.toLocaleString()}
            </p>
            {row.original.attachmentCount > 0 && (
              <div className="text-muted-foreground flex items-center justify-end gap-1 text-xs">
                <Paperclip className="h-3 w-3" />
                <span className="tabular-nums">
                  {row.original.attachmentCount.toLocaleString()}
                </span>
              </div>
            )}
          </div>
        ),
      },
    ],
    [toggleChannel, getChannelSelectionState],
  );

  const table = useReactTable({
    data: processedData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    initialState: {
      pagination: {
        pageSize: 100,
      },
    },
    state: {
      sorting,
      columnFilters,
    },
  });

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (document.activeElement?.tagName === "INPUT") return;

      switch (e.key) {
        case "ArrowLeft":
          if (table.getCanPreviousPage()) {
            table.previousPage();
          }
          break;
        case "ArrowRight":
          if (table.getCanNextPage()) {
            table.nextPage();
          }
          break;
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [table]);

  const handleBulkSelection = async (shouldSelect: boolean) => {
    if (isProcessing || !processedData.length) return;

    setIsProcessing(true);
    try {
      await processInChunks(processedData, async (chunk) => {
        chunk.forEach((channel) => {
          toggleChannel(channel.id, channel.messages, shouldSelect);
        });
      });
    } finally {
      setIsProcessing(false);
    }
  };

  useEffect(() => {
    const CHUNK_SIZE = 1000;
    let isCurrentOperation = true;

    const processChannels = async () => {
      setIsProcessing(true);

      try {
        const searchTermFilter = filters.terms
          ? (() => {
              const parsed = parseSearchQuery(filters.terms);
              if (
                parsed.exactPhrases.length === 0 &&
                parsed.looseTerms.length === 0
              ) {
                return null;
              }
              const sanitizedNeedle = sanitizeForSearch(filters.terms);

              if (parsed.hasQuotes) {
                return (text: string) => matchesSearchQuery(text, parsed);
              }

              return sanitizedNeedle
                ? (text: string) => text.includes(sanitizedNeedle)
                : null;
            })()
          : null;

        const typeFilter =
          filters.channelTypes.length > 0
            ? (type: string) => filters.channelTypes.includes(type)
            : null;

        const serverFilter =
          filters.serverNames.length > 0
            ? (server: string | undefined) =>
                server && filters.serverNames.includes(server)
            : null;

        const trimmedSearchTerm = filters.terms?.trim() ?? "";
        const rawSearchTermExists = trimmedSearchTerm.length > 0;
        const parsedSearchQuery = rawSearchTermExists
          ? parseSearchQuery(trimmedSearchTerm)
          : null;
        const sanitizedNeedle =
          parsedSearchQuery && !parsedSearchQuery.hasQuotes
            ? sanitizeForSearch(trimmedSearchTerm)
            : "";
        const hasSearchTerm = parsedSearchQuery?.hasQuotes
          ? parsedSearchQuery.exactPhrases.length > 0 ||
            parsedSearchQuery.looseTerms.length > 0
          : Boolean(sanitizedNeedle);

        const result: ProcessedChannel[] = [];

        for (let i = 0; i < channels.length; i += CHUNK_SIZE) {
          if (!isCurrentOperation) break;

          const channelChunk = channels.slice(i, i + CHUNK_SIZE);

          for (const channel of channelChunk) {
            if (searchTermFilter && !searchTermFilter(channel.searchableText))
              continue;
            if (typeFilter && !typeFilter(channel.type)) continue;
            if (serverFilter && !serverFilter(channel.serverName)) continue;

            const isGuildChannel = channel.type.startsWith("GUILD_");
            const isLeftServer = isGuildChannel && !channel.serverName;

            if (filters.showLeftServers && !isLeftServer) {
              continue;
            }

            let filteredMessageCount = 0;
            let filteredAttachmentCount = 0;
            const filteredMessages: typeof channel.messages = [];

            const fromTime = filters.dateRange?.from?.getTime() ?? -Infinity;
            const toTime = filters.dateRange?.to?.getTime() ?? Infinity;

            const channelMetaText = sanitizeForSearch(
              [
                channel.displayName,
                channel.serverName,
                channel.channelCategory,
                channel.participants?.join(" "),
                channel.originalName,
              ]
                .filter(Boolean)
                .join(" "),
            );

            const channelMetaMatches =
              hasSearchTerm && searchTermFilter
                ? searchTermFilter(channelMetaText)
                : false;

            for (let j = 0; j < channel.messages.length; j += CHUNK_SIZE) {
              const messageChunk = channel.messages.slice(j, j + CHUNK_SIZE);

              for (const message of messageChunk) {
                const messageDate = new Date(message.Timestamp).getTime();
                const withinDateRange =
                  messageDate >= fromTime && messageDate <= toTime;
                const hasAttachment = filters.hasAttachments
                  ? !!message.Attachments
                  : true;

                const matchesSearchTerms =
                  !hasSearchTerm ||
                  channelMetaMatches ||
                  (parsedSearchQuery && parsedSearchQuery.hasQuotes
                    ? matchesSearchQuery(
                        sanitizeForSearch(message.Contents ?? ""),
                        parsedSearchQuery,
                      )
                    : sanitizeForSearch(message.Contents ?? "").includes(
                        sanitizedNeedle,
                      ));

                if (withinDateRange && hasAttachment && matchesSearchTerms) {
                  filteredMessages.push(message);
                  filteredMessageCount++;
                  if (message.Attachments) filteredAttachmentCount++;
                }
              }
            }

            if (filteredMessageCount > 0) {
              result.push({
                ...channel,
                messages: filteredMessages,
                messageCount: filteredMessageCount,
                attachmentCount: filteredAttachmentCount,
              });
            }
          }

          await new Promise((resolve) => setTimeout(resolve, 0));
        }

        if (isCurrentOperation) {
          setProcessedData(result);
        }
      } finally {
        if (isCurrentOperation) {
          setIsProcessing(false);
        }
      }
    };

    processChannels();

    return () => {
      isCurrentOperation = false;
    };
  }, [channels, filters]);

  const tableBody = isProcessing ? (
    <TableRow>
      <TableCell colSpan={columns.length} className="h-32 text-center">
        <div className="text-muted-foreground flex flex-col items-center justify-center gap-2">
          <Loader2 className="h-8 w-8 animate-spin" />
          <div className="text-sm">Processing channels...</div>
        </div>
      </TableCell>
    </TableRow>
  ) : (
    <TableBody>
      {table.getRowModel().rows?.length ? (
        table.getRowModel().rows.map((row) => {
          const selectionState = getChannelSelectionState(
            row.original.id,
            row.original.messages,
          );

          return (
            <TableRow
              key={row.id}
              data-state={selectionState.selected ? "selected" : undefined}
              className={cn(
                "group hover:bg-accent h-16 min-h-[64px] cursor-pointer transition-colors",
                selectionState.indeterminate && "bg-accent/50",
                selectedChannel?.id === row.original.id && "bg-accent/70",
              )}
              onClick={() => onChannelSelect?.(row.original)}
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell
                  key={cell.id}
                  style={{
                    width: `${cell.column.getSize()}px`,
                    maxWidth: `${cell.column.getSize()}px`,
                    minWidth: `${cell.column.getSize()}px`,
                  }}
                  className="px-4 py-2"
                >
                  <div className="w-full overflow-hidden">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </div>
                </TableCell>
              ))}
            </TableRow>
          );
        })
      ) : (
        <TableRow>
          <TableCell colSpan={columns.length} className="h-32 text-center">
            <div className="text-muted-foreground flex flex-col items-center justify-center gap-2">
              <FolderSearch className="h-8 w-8" />
              <div className="text-sm">No channels found</div>
              {hasActiveFilters ? (
                <>
                  <div className="text-muted-foreground text-xs">
                    Filters in effect:
                  </div>
                  <div className="flex flex-wrap justify-center gap-2 text-[11px] text-slate-600 dark:text-slate-400">
                    {activeFilters.map((filter) => (
                      <span
                        key={`${filter.label}-${filter.detail ?? "active"}`}
                        className="rounded-full border border-slate-300 px-2 py-0.5 text-[11px] dark:border-slate-700"
                      >
                        {filter.detail
                          ? `${filter.label}: ${filter.detail}`
                          : filter.label}
                      </span>
                    ))}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-1 px-3 py-1.5 text-xs"
                    onClick={clearFilters}
                  >
                    Clear filters
                  </Button>
                </>
              ) : (
                <div className="text-xs">
                  Try adjusting your search or filters
                </div>
              )}
            </div>
          </TableCell>
        </TableRow>
      )}
    </TableBody>
  );

  return (
    <div className="bg-card relative flex h-full w-3/5 max-w-xl min-w-md flex-col rounded-lg border">
      <ScrollArea className="flex-1">
        <div className="w-[580px]">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      style={{
                        width: `${header.getSize()}px`,
                        maxWidth: `${header.getSize()}px`,
                        minWidth: `${header.getSize()}px`,
                      }}
                      className="h-12 px-4"
                    >
                      <div className="w-full overflow-hidden">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                      </div>
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>

            {tableBody}
          </Table>
        </div>
      </ScrollArea>

      <div className="flex min-h-[60px] items-center justify-between border-t px-4 py-3">
        <div className="flex shrink-0 items-center gap-2">
          <Select
            value={`${table.getState().pagination.pageSize}`}
            onValueChange={(value) => {
              table.setPageSize(Number(value));
            }}
          >
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue placeholder={table.getState().pagination.pageSize} />
            </SelectTrigger>
            <SelectContent side="top">
              {[10, 20, 50, 100].map((pageSize) => (
                <SelectItem key={pageSize} value={`${pageSize}`}>
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex flex-1 items-center justify-center px-2">
            <div className="flex items-center gap-2 text-sm">
              <span className="font-medium">
                {table.getState().pagination.pageIndex + 1}
              </span>
              <span className="text-muted-foreground">
                of {table.getPageCount()}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              className="h-8 gap-1"
              onClick={() => handleBulkSelection(true)}
              disabled={isProcessing || !processedData.length}
            >
              {isProcessing ? (
                <>
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Selecting...
                </>
              ) : (
                <>
                  <CheckSquare className="h-3 w-3" />
                  Select All ({processedData.length.toLocaleString()})
                </>
              )}
            </Button>

            <Button
              variant="outline"
              size="sm"
              className="h-8 gap-1"
              onClick={() => handleBulkSelection(false)}
              disabled={isProcessing || !processedData.length}
            >
              {isProcessing ? (
                <>
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Clearing...
                </>
              ) : (
                <>
                  <X className="h-3 w-3" />
                  Clear All
                </>
              )}
            </Button>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            className="hidden h-8 w-8 lg:flex"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
          >
            <span className="sr-only">Go to first page</span>
            <ChevronsLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            <span className="sr-only">Go to previous page</span>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            <span className="sr-only">Go to next page</span>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="hidden h-8 w-8 lg:flex"
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
          >
            <span className="sr-only">Go to last page</span>
            <ChevronsRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
